<template>
  <div class="crud_form_wrapper single_show_content_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.show_horse_purchase_order") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #F7941E">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <div v-if="loading" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else>
        <!-- ==== Start:: Order Data Section ==== -->
        <div class="section-card mb-4">
          <h5 class="section-title">{{ $t("PLACEHOLDERS.order_data") }}</h5>
          <div class="row">
            <!-- Order Number -->
            <base-input
              col="4"
              type="text"
              :placeholder="$t('PLACEHOLDERS.order_number')"
              :value="'#' + data.id"
              disabled
            />
            
            <!-- Order Date -->
            <base-input
              col="4"
              type="text"
              :placeholder="$t('PLACEHOLDERS.order_date')"
              :value="formatDate(data.created_at)"
              disabled
            />
            
            <!-- Order Status -->
            <div class="col-4 my-5">
              <label class="form-label">{{ $t("PLACEHOLDERS.order_status") }}</label>
              <v-chip
                :color="getStatusColor(data.status)"
                text-color="white"
                large
              >
                {{ data.status_label }}
              </v-chip>
            </div>
          </div>
        </div>
        <!-- ==== End:: Order Data Section ==== -->

        <!-- ==== Start:: Client Data Section ==== -->
        <div class="section-card mb-4" v-if="data.user">
          <h5 class="section-title">{{ $t("PLACEHOLDERS.client_data") }}</h5>
          <div class="row">
            <!-- Client Avatar -->
            <div v-if="data.user.avatar" class="col-12 mb-3">
              <div class="preview-container text-center">
                <img :src="data.user.avatar" :alt="data.user.name" class="user-avatar" />
              </div>
            </div>

            <!-- Client Name -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.client_name')"
              :value="data.user.name"
              disabled
            />
            <!-- mobile Name -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.phone')"
              :value="data.user.mobile"
              disabled
            />

            <!-- Client Rating -->
            <div class="col-6 my-5">
              <label class="form-label">{{ $t("PLACEHOLDERS.rating") }}</label>
              <div class="rating-display">
                <v-rating
                  :value="data.user.rating"
                  color="yellow darken-3"
                  background-color="grey darken-1"
                  readonly
                  dense
                ></v-rating>
                <span class="rating-text">{{ data.user.rating }}/5</span>
              </div>
            </div>

            <!-- Client City -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.city')"
              :value="data.user.city || $t('TABLES.noData')"
              disabled
            />

            <!-- Client Location on Map -->
            <div class="col-12" v-if="data.user.latitude && data.user.longitude">
              <h6 class="mb-3">{{ $t("PLACEHOLDERS.client_location") }}</h6>
              <div class="map_wrapper">
                <GmapMap
                  :center="{
                    lat: parseFloat(data.user.latitude),
                    lng: parseFloat(data.user.longitude),
                  }"
                  :zoom="13"
                  map-type-id="terrain"
                  style="width: 100%; height: 300px"
                >
                  <GmapMarker
                    :position="{
                      lat: parseFloat(data.user.latitude),
                      lng: parseFloat(data.user.longitude)
                    }"
                    :clickable="false"
                    :draggable="false"
                  />
                </GmapMap>
              </div>
            </div>
          </div>
        </div>
        <!-- ==== End:: Client Data Section ==== -->

        <!-- ==== Start:: Horse Owner Data Section ==== -->
        <div class="section-card mb-4" v-if="data.provider">
          <h5 class="section-title">{{ $t("HORSE_PURCHASE.horse_owner_data") }}</h5>
          <div class="row">
            <!-- Horse Owner Avatar -->
            <div v-if="data.provider.avatar" class="col-12 mb-3">
              <div class="preview-container text-center">
                <img :src="data.provider.avatar" :alt="data.provider.name" class="stable-avatar" />
              </div>
            </div>

            <!-- Horse Owner Name -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('HORSE_PURCHASE.horse_owner_name')"
              :value="data.provider.name"
              disabled
            />
            <!-- Horse Owner Mobile -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.phone')"
              :value="data.provider.mobile"
              disabled
            />

            <!-- Horse Owner Rating -->
            <div class="col-6 my-5">
              <label class="form-label">{{ $t("PLACEHOLDERS.rating") }}</label>
              <div class="rating-display">
                <v-rating
                  :value="data.provider.rating"
                  color="yellow darken-3"
                  background-color="grey darken-1"
                  readonly
                  dense
                ></v-rating>
                <span class="rating-text">{{ data.provider.rating }}/5</span>
              </div>
            </div>

            <!-- Horse Owner City -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.city')"
              :value="data.provider.city || $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Owner Location on Map -->
            <div class="col-12" v-if="data.provider.latitude && data.provider.longitude">
              <h6 class="mb-3">{{ $t("PLACEHOLDERS.location") }}</h6>
              <div class="map_wrapper">
                <GmapMap
                  :center="{
                    lat: parseFloat(data.provider.latitude),
                    lng: parseFloat(data.provider.longitude),
                  }"
                  :zoom="13"
                  map-type-id="terrain"
                  style="width: 100%; height: 300px"
                >
                  <GmapMarker
                    :position="{
                      lat: parseFloat(data.provider.latitude),
                      lng: parseFloat(data.provider.longitude)
                    }"
                    :clickable="false"
                    :draggable="false"
                  />
                </GmapMap>
              </div>
            </div>
          </div>
        </div>
        <!-- ==== End:: Horse Owner Data Section ==== -->

        <!-- ==== Start:: Horse Data Section ==== -->
        <div class="section-card mb-4" v-if="data.horse">
          <h5 class="section-title">{{ $t("HORSE_PURCHASE.horse_data") }}</h5>
          <div class="row">
            <!-- Horse Images -->
            <div class="col-12" v-if="data.horse.images && data.horse.images.length > 0">
              <h6 class="mb-3">{{ $t("PLACEHOLDERS.horse_images") }}</h6>
              <div class="images-container">
                <div
                  v-for="(image, index) in data.horse.images"
                  :key="index"
                  class="image-wrapper"
                  @click="showImageModal(image)"
                >
                  <img :src="image" :alt="`Horse Image ${index + 1}`" class="horse-image" />
                </div>
              </div>
            </div>

            <!-- Horse Video -->
            <div class="col-12" v-if="data.horse.video">
              <h6 class="mb-3">{{ $t("PLACEHOLDERS.horse_video") }}</h6>
              <video controls style="width: 100%; max-height: 400px;">
                <source :src="data.horse.video" type="video/mp4">
                {{ $t("MESSAGES.video_not_supported") }}
              </video>
            </div>

            <!-- Horse Name -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.horse_name')"
              :value="data.horse.name"
              disabled
            />

            <!-- Horse Rating -->
            <div class="col-6 my-5">
              <label class="form-label">{{ $t("PLACEHOLDERS.horse_rating") }}</label>
              <div class="rating-display">
                <v-rating
                  :value="data.horse.rating"
                  color="yellow darken-3"
                  background-color="grey darken-1"
                  readonly
                  dense
                ></v-rating>
                <span class="rating-text">{{ data.horse.rating }}/5</span>
              </div>
            </div>

            <!-- Horse Breed -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.breed')"
              :value="data.horse.breed || $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Gender -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.gender')"
              :value="data.horse.gender || $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Age -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.age')"
              :value="data.horse.age ? data.horse.age + ' ' + $t('PLACEHOLDERS.years') : $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Color -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.color')"
              :value="data.horse.color || $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Height -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.height')"
              :value="data.horse.height ? data.horse.height + ' ' + $t('PLACEHOLDERS.cm') : $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Father -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.father')"
              :value="data.horse.father || $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Mother -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.mother')"
              :value="data.horse.mother || $t('TABLES.noData')"
              disabled
            />

            <!-- Chip Number -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.chip_number')"
              :value="data.horse.chip_number || $t('TABLES.noData')"
              disabled
            />

            <!-- Health Status -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.health_status')"
              :value="data.horse.health_status || $t('TABLES.noData')"
              disabled
            />

            <!-- Training Level -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.training_level')"
              :value="data.horse.training_level || $t('TABLES.noData')"
              disabled
            />

            <!-- Sell Price -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('HORSE_PURCHASE.sell_price')"
              :value="data.horse.sell_price ? data.horse.sell_price + ' ' + $t('PLACEHOLDERS.riyal') : $t('TABLES.noData')"
              disabled
            />

            <!-- Pedigree Certificate -->
            <div class="col-6" v-if="data.horse.pedigree_certificate">
              <h6 class="mb-3">{{ $t("HORSE_PURCHASE.pedigree_certificate") }}</h6>
              <a :href="data.horse.pedigree_certificate" target="_blank" class="btn btn-info">
                <i class="fal fa-download"></i> {{ $t("PLACEHOLDERS.download") }}
              </a>
            </div>

            <!-- Delivery Included -->
            <div class="col-6 my-5">
              <label class="form-label">{{ $t("HORSE_PURCHASE.delivery_included") }}</label>
              <v-chip :color="data.horse.delivery_included ? 'green' : 'red'" text-color="white">
                {{ data.horse.delivery_included ? $t("PLACEHOLDERS.yes") : $t("PLACEHOLDERS.no") }}
              </v-chip>
            </div>

            <!-- Cancellation Policy -->
            <div class="col-12" v-if="data.horse.cancellation_policy">
              <h6 class="mb-3">{{ $t("HORSE_PURCHASE.cancellation_policy") }}</h6>
              <a :href="data.horse.cancellation_policy" target="_blank" class="btn btn-info">
                <i class="fal fa-file-pdf"></i> {{ $t("HORSE_RENT.view_policy") }}
              </a>
            </div>
          </div>
        </div>
        <!-- ==== End:: Horse Data Section ==== -->

        <!-- ==== Start:: Purchase Data Section ==== -->
        <div class="section-card mb-4">
          <h5 class="section-title">{{ $t("HORSE_PURCHASE.purchase_data") }}</h5>
          <div class="row">
            <!-- Receive Method -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('HORSE_PURCHASE.receive_method')"
              :value="data.receive_method_label || $t('TABLES.noData')"
              disabled
            />

            <!-- Experience Level -->
            <base-input
              v-if="data.experience_level"
              col="6"
              type="text"
              :placeholder="$t('HORSE_PURCHASE.experience_level')"
              :value="data.experience_level"
              disabled
            />

            <!-- Client Location -->
            <base-input
              v-if="data.location"
              col="12"
              type="text"
              :placeholder="$t('HORSE_PURCHASE.client_location')"
              :value="data.location"
              disabled
            />

            <!-- Client Location on Map (only for delivery to client location) -->
            <div class="col-12" v-if="data.receive_method === 'owner_location' && data.latitude && data.longitude">
              <h6 class="mb-3">{{ $t("HORSE_PURCHASE.delivery_location") }}</h6>
              <div class="map_wrapper">
                <GmapMap
                  :center="{
                    lat: parseFloat(data.latitude),
                    lng: parseFloat(data.longitude),
                  }"
                  :zoom="13"
                  map-type-id="terrain"
                  style="width: 100%; height: 300px"
                >
                  <GmapMarker
                    :position="{
                      lat: parseFloat(data.latitude),
                      lng: parseFloat(data.longitude)
                    }"
                    :clickable="false"
                    :draggable="false"
                  />
                </GmapMap>
              </div>
            </div>

            <!-- Notes -->
            <base-input
              v-if="data.notes"
              col="12"
              type="textarea"
              :placeholder="$t('PLACEHOLDERS.notes')"
              :value="data.notes"
              disabled
            />

            <!-- Subtotal -->
            <base-input
              col="4"
              type="text"
              :placeholder="$t('HORSE_PURCHASE.subtotal')"
              :value="data.sub_total ? data.sub_total + ' ' + $t('PLACEHOLDERS.riyal') : $t('TABLES.noData')"
              disabled
            />

            <!-- Tax -->
            <base-input
              col="4"
              type="text"
              :placeholder="$t('PLACEHOLDERS.tax')"
              :value="data.tax ? data.tax + ' ' + $t('PLACEHOLDERS.riyal') : $t('TABLES.noData')"
              disabled
            />

            <!-- Commission -->
            <base-input
              col="4"
              type="text"
              :placeholder="$t('HORSE_PURCHASE.commission')"
              :value="data.commission ? data.commission + ' ' + $t('PLACEHOLDERS.riyal') : $t('TABLES.noData')"
              disabled
            />

            <!-- Total Price -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.total_price')"
              :value="data.total ? data.total + ' ' + $t('PLACEHOLDERS.riyal') : $t('TABLES.noData')"
              disabled
            />

            <!-- Payment Status -->
            <div class="col-6 my-5">
              <label class="form-label">{{ $t("HORSE_PURCHASE.payment_status") }}</label>
              <v-chip :color="data.is_paid ? 'green' : 'orange'" text-color="white">
                {{ data.is_paid ? $t("HORSE_PURCHASE.paid") : $t("HORSE_PURCHASE.unpaid") }}
              </v-chip>
            </div>

            <!-- Payment Method -->
            <base-input
              v-if="data.payment_method"
              col="6"
              type="text"
              :placeholder="$t('HORSE_PURCHASE.payment_method')"
              :value="data.payment_method"
              disabled
            />
          </div>
        </div>
        <!-- ==== End:: Purchase Data Section ==== -->

        <!-- ==== Start:: Messages Section (for current and completed orders only) ==== -->
        <!-- <div class="section-card mb-4" v-if="shouldShowMessages">
          <h5 class="section-title">{{ $t("HORSE_PURCHASE.messages") }}</h5>
          <div v-if="!hasMessages" class="empty-state text-center py-4">
            <p class="empty-text">{{ $t('HORSE_PURCHASE.no_messages') }}</p>
          </div>
          <div v-else class="messages-container">
            <div
              v-for="(message, index) in messages"
              :key="index"
              class="message-item mb-3 p-3"
              style="border: 1px solid #e9ecef; border-radius: 8px; background: #f8f9fa;"
            >
              <div class="message-header d-flex justify-content-between align-items-start mb-2">
                <div class="sender-info d-flex align-items-center">
                  <img 
                    v-if="message.sender_avatar" 
                    :src="message.sender_avatar" 
                    :alt="message.sender_name"
                    class="sender-avatar me-2"
                    style="width: 40px; height: 40px; border-radius: 50%; object-fit: cover;"
                  />
                  <div class="sender-details">
                    <h6 class="sender-name mb-0">{{ message.sender_name }}</h6>
                    <p class="message-subject mb-0 text-muted small">{{ message.subject }}</p>
                  </div>
                </div>
                <div class="message-time text-muted small">
                  <span class="message-date">{{ formatDate(message.created_at) }}</span>
                  <br>
                  <span class="message-time-detail">{{ formatTime(message.created_at) }}</span>
                </div>
              </div>
              <div class="message-content">
                <p class="mb-0">{{ message.content }}</p>
              </div>
            </div>
          </div>
          <div v-if="data.status === 'in_progress'" class="message-form mt-4 p-3" style="background: #f1f3f4; border-radius: 8px;">
            <h6 class="mb-3">{{ $t("HORSE_PURCHASE.send_message") }}</h6>
            <div class="row">
              <div class="col-12">
                <textarea
                  v-model="newMessage"
                  :placeholder="$t('HORSE_PURCHASE.message_content')"
                  rows="3"
                  class="form-control mb-3"
                  style="resize: vertical;"
                ></textarea>
              </div>
              <div class="col-12">
                <button 
                  type="button"
                  class="btn btn-primary"
                  @click="sendMessage"
                  :disabled="!newMessage.trim() || sendingMessage"
                >
                  <i v-if="sendingMessage" class="fas fa-spinner fa-spin me-1"></i>
                  {{ $t('HORSE_PURCHASE.send') }}
                </button>
              </div>
            </div>
          </div>
        </div> -->
        <!-- ==== End:: Messages Section ==== -->

        <!-- Cancellation Reason (if cancelled or rejected) -->
        <!-- <div class="section-card mb-4" v-if="data.cancellation_reason && (data.status === 'cancelled' || data.status === 'rejected')">
          <h5 class="section-title">{{ $t("HORSE_PURCHASE.cancellation_reason") }}</h5>
          <div class="row">
            <base-input
              col="12"
              type="textarea"
              :placeholder="$t('HORSE_PURCHASE.cancellation_reason')"
              :value="data.cancellation_reason"
              disabled
            />
          </div>
        </div> -->
      </div>
    </div>
    <!-- End:: Single Step Form Content -->

    <!-- Start:: Image Modal -->
    <v-dialog v-model="dialogImage" max-width="800px">
      <v-card>
        <v-card-text class="text-center">
          <img :src="selectedImage" style="max-width: 100%; max-height: 70vh;" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialogImage = false">{{ $t("BUTTONS.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Image Modal -->
  </div>
</template>

<script>
export default {
  name: "ShowHorsePurchaseOrder",

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      // End:: Loading Data

      // Start:: Image Modal
      dialogImage: false,
      selectedImage: null,
      // End:: Image Modal

      // Start:: Data
      data: {
        id: null,
        status: null,
        status_label: '',
        created_at: null,
        user: null,
        provider: null,
        horse: null,
        experience_level: null,
        location: null,
        notes: null,
        sub_total: null,
        tax: null,
        commission: null,
        total: null,
        is_paid: null,
        payment_method: null,
        cancellation_reason: null,
        receive_method: null,
        receive_method_label: null,
        latitude: null,
        longitude: null,
      },
      // End:: Data

      // Start:: Messages Data
      messages: [],
      newMessage: '',
      sendingMessage: false,
      // End:: Messages Data
    };
  },

  computed: {
    shouldShowMessages() {
      return this.data.status === 'in_progress' || this.data.status === 'completed';
    },

    hasMessages() {
      return this.messages && this.messages.length > 0;
    }
  },

  methods: {
    // Start:: Helper Methods
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US');
    },

    formatTime(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleTimeString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getStatusColor(status) {
      const statusColors = {
        new: 'blue',
        accepted: 'green',
        rejected: 'red',
        in_progress: 'orange',
        completed: 'teal',
        cancelled: 'grey'
      };
      return statusColors[status] || 'grey';
    },

    showImageModal(image) {
      this.selectedImage = image;
      this.dialogImage = true;
    },
    // End:: Helper Methods

    // Start:: Message Methods
    async sendMessage() {
      if (!this.newMessage.trim()) return;
      
      this.sendingMessage = true;
      try {
        const response = await this.$axios.post(`/horse-purchase-orders/${this.$route.params.id}/messages`, {
          content: this.newMessage
        });
        
        if (response.data.success) {
          // Add the new message to the messages array
          this.messages.push(response.data.data);
          this.newMessage = '';
          this.$message.success(this.$t('HORSE_PURCHASE.message_sent'));
        }
      } catch (error) {
        this.$message.error(this.$t('HORSE_PURCHASE.message_error'));
      } finally {
        this.sendingMessage = false;
      }
    },

    async getMessages() {
      try {
        const response = await this.$axios.get(`/horse-purchase-orders/${this.$route.params.id}/messages`);
        if (response.data.success) {
          this.messages = response.data.data;
        }
      } catch (error) {
        console.log('Error loading messages:', error);
      }
    },
    // End:: Message Methods

    // Start:: Get Data To Show
    async getDataToShow() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: `orders/${this.$route.params.id}`,
        });
        this.data = res.data.data.order;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || "Error loading order data");
        this.$message.error(error.response?.data?.message);
        this.$router.push({ path: "/horse-purchase/all" });
      }
    },
    // End:: Get Data To Show
  },

  created() {
    // Start:: Fire Methods
    this.getDataToShow();
    if (this.shouldShowMessages) {
      this.getMessages();
    }
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.section-card {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.section-title {
  color: #2c3e50;
  border-bottom: 2px solid #3498db;
  padding-bottom: 8px;
  margin-bottom: 20px;
  font-weight: bold;
}

.preview-container {
  margin-bottom: 20px;
}

.user-avatar, .stable-avatar {
  max-width: 120px;
  max-height: 120px;
  border: 3px solid #ddd;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.rating-display {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rating-text {
  font-weight: bold;
  color: #f39c12;
}

.map_wrapper {
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #eee;
  margin-top: 10px;
}

.images-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 5px;
}

.image-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  cursor: pointer;
}

.image-wrapper:hover {
  transform: scale(1.05);
}

.horse-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  transition: opacity 0.3s ease;
}

.horse-image:hover {
  opacity: 0.9;
}

@media (max-width: 768px) {
  .images-container {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }
  
  .horse-image {
    height: 120px;
  }
  
  .user-avatar, .stable-avatar {
    max-width: 100px;
    max-height: 100px;
  }
}
</style>